import React, { useState, useEffect } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import FooterCard from "components/footers/PageFooter";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton } from "components/misc/Buttons";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import PageHeader from "components/headers/PageHeader";
import { Editor } from 'primereact/editor';
import BlogIllustrate from "images/blog_ills.jpg";
import { FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV;

const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto shadow-md mb-4`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`
]);
const HeadingRow = tw.div`flex justify-center`;
const Heading = tw(SectionHeading)`hover:text-[#0049AC] text-xl mt-0 sm:text-2xl md:text-2xl`;
const FormSection = tw.div`bg-white rounded px-10 pt-6 pb-8 mb-4 lg:w-2/3 py-2 sm:w-full`;
const Input = tw.input`border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline`;
const Textarea = tw.textarea`border rounded w-full py-2 px-3 text-gray-700 h-24 focus:outline-none focus:shadow-outline`;
const SelectInput = styled.input`
  ${tw`border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline`}
`;
const Label = tw.label`block text-gray-700 text-sm font-bold mb-2`;
const Button = tw(PrimaryButton)`absolute right-0 top-0 px-4 py-2 text-sm`;
const ThumbnailButton = styled(Button)`
  ${tw`bg-blue-500 text-white`}
`;
const AddThumbnailContainer = tw.div`relative flex items-center`;
const BlogContentContainer = tw.div`w-full`;
const BlogContentEditor = tw.div`mb-8`;
const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8 w-40 flex justify-center items-center`;
const ProgressCircle = tw.div`w-10 h-10 border-4 border-t-4 border-[#0049AC] rounded-full animate-spin`;
const DialogBox = styled.div`
  ${tw`fixed top-0 left-0 w-full h-full flex justify-center items-center bg-gray-500 bg-opacity-50 z-50`}
  display: ${({ show }) => (show ? "flex" : "none")};
  animation: fadeIn 0.3s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
const Modal = tw.div`fixed z-20 inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center`;
const ModalContent = tw.div`bg-white p-6 rounded shadow-md w-96`;
const DialogContent = tw.div`bg-white p-6 rounded-lg shadow-xl text-center w-[90%] max-w-md`;
const DialogHeading = tw.h2`text-xl font-bold text-gray-900`;
const DialogMessage = tw.p`mt-4 text-sm text-gray-700`;
const OkButton = tw.button`mt-4 px-4 py-2 bg-[#0049AC] text-white rounded-full`;

const CloseButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      border: 'none',
      fontSize: '30px',
      cursor: 'pointer',
      position: 'absolute',
      color: '#333',
      marginLeft: '330px',
      marginTop: '-30px'
    }}
    aria-label="Close modal"
  >
    ×
  </button>
);

export default () => {
  const [categories] = useState(["Destinations", "Lifestyle", "Travel", "Hotel/Resort", "Package"]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: "", thumbnail: "", description: "", category: "", hashtags: "" });
  const [userForm, setUserForm] = useState({
    name: "",
    mobile: "",
    email: "",
    designation: "",
    profile_pic: "",
    city: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState(false);
  const [content, setContent] = useState(null);
  const [profile, setProfile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [headStick, setHeadStick] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    const storedUser = getUserDetails();
    if (storedUser) setUserForm(storedUser);
  }, []);

  const handleBlogDataChange = (e) => {
    const { name, value } = e.target;
    setBlogForm({ ...blogForm, [name]: value });
  };

  const handleUserDataChange = (e) => {
    const { name, value } = e.target;
    setUserForm({ ...userForm, [name]: value });
  };

  const validateBlogForm = () => {
    if (content == null) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please write blog content.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (countWords(content) <= 50) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Blog content should be minimum 50 words.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (blogForm.title === "") {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please add blog title.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (blogForm.thumbnail === "" && thumbnail == null) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please upload blog thumbnail or provide a link.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (blogForm.description === "") {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Write short summary/description about blog.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (countWords(blogForm.description) <= 20) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Summary/description must contain 20 words.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (blogForm.category === "") {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please select blog category.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (blogForm.hashtags.trim() === "") {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please add valid hashtags.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    return true;
  };

  const validateUserForm = (requireProfile = false) => {
    if (userForm.name === "") {
      setFieldError(true);
      setShowDialog(true);
      setMessage('Write your name.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
      setFieldError(true);
      setShowDialog(true);
      setMessage('Please input correct email.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (!/^\d{10}$/.test(userForm.mobile)) {
      setFieldError(true);
      setShowDialog(true);
      setMessage('Please input correct mobile number.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (userForm.designation === "") {
      setFieldError(true);
      setShowDialog(true);
      setMessage('Write your designation.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (userForm.city === "") {
      setFieldError(true);
      setShowDialog(true);
      setMessage('Write your city.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    if (requireProfile && userForm.profile_pic === "" && profile == null) {
      setFieldError(true);
      setShowDialog(true);
      setMessage('Please add your profile picture or provide a link.');
      document.body.style.overflow = 'hidden';
      return false;
    }
    return true;
  };

  const isUserDataComplete = (user) => {
    return (
      user &&
      user.name &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email) &&
      /^\d{10}$/.test(user.mobile) &&
      user.designation &&
      user.city
    );
  };

  const saveUserDetails = (userDetails) => {
    localStorage.setItem("userDetails", JSON.stringify(userDetails));
  };

  const getUserDetails = () => {
    const storedUser = localStorage.getItem("userDetails");
    return storedUser ? JSON.parse(storedUser) : null;
  };

  const handleProfileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      setFieldError(true);
      setShowDialog(true);
      setMessage("Profile picture file size exceeds 5MB.");
      document.body.style.overflow = 'hidden';
      return;
    }
    setProfile(file);
  };

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      setFieldError(true);
      setShowDialog(true);
      setMessage("Thumbnail file size exceeds 5MB.");
      document.body.style.overflow = 'hidden';
      return;
    }
    setThumbnail(file);
  };

  const handleBlogSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const storedUser = getUserDetails();
    if (!storedUser || !isUserDataComplete(storedUser)) {
      setIsModalOpen(true);
      setHeadStick(false);
      document.body.style.overflow = 'hidden';
      return;
    }
    if (validateBlogForm()) {
      setUserForm(storedUser);
      handleSubmit();
    }
  };

  const handleUserSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (validateUserForm(true)) {
      handleSubmit();
    }
  };

  const handleCloseDialog = () => {
    document.body.style.overflow = 'auto';
    setShowDialog(false);
    setMessage('');
    setFieldError(false);
  };

  const handleClose = () => {
    document.body.style.overflow = 'auto';
    setIsModalOpen(false);
    setShowDialog(false);
    setHeadStick(null);
    setIsSubmitting(false);
  };

  const countWords = (text) => {
    if (!text || typeof text !== "string") return 0;
    return text.trim().split(/\s+/).length;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('blogData', JSON.stringify({ ...blogForm, content }));
      formData.append('userData', JSON.stringify(userForm));
      if (thumbnail) formData.append('thumbnail', thumbnail);
      if (profile) formData.append('profile', profile);

      const response = await fetch(`${API_URL}/web/blogs/submit-blog`, {
        method: "POST",
        headers: {
          'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
        },
        body: formData,
      });

      const result = await response.json();
      if (response.ok && result.header.resCode === 200 && result.header.resMessage === "success") {
        setIsSuccess(true);
        saveUserDetails(userForm);
        setProfile(null);
        setThumbnail(null);
        setContent(null);
        setBlogForm({ title: "", thumbnail: "", description: "", category: "", hashtags: "" });
      } else {
        setIsSuccess(false);
        setMessage(result.header.resDetail || 'Some error occurred.');
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage('Some error occurred.');
      setFieldError(false);
    } finally {
      setIsModalOpen(false);
      setIsSubmitting(false);
      setShowDialog(true);
    }
  };

  return (
    <AnimationRevealPage>
      <PageHeader pageName="Blogs" titleName="Write Blog" headStick={headStick} />
      <Container>
        <br />
        <HeadingRow>
          <Heading>Craft your masterpiece now !</Heading>
        </HeadingRow>
        <br />
        <BlogContentContainer>
          <BlogContentEditor>
            <Editor
              value={content}
              onTextChange={(e) => setContent(e.htmlValue)}
              placeholder="Start writing your experience through words.."
              style={{ height: "600px" }}
              aria-label="Blog content editor"
            />
          </BlogContentEditor>
        </BlogContentContainer>
        <TwoColumn>
          <FormSection>
            <div>
              <Label htmlFor="title">Blog Title</Label>
              <Input
                id="title"
                type="text"
                name="title"
                placeholder="Enter blog title"
                value={blogForm.title}
                onChange={handleBlogDataChange}
                required
                aria-label="Blog title"
              />
            </div>
            <br />
            <div>
              <Label htmlFor="thumbnail">Thumbnail</Label>
              <AddThumbnailContainer>
                <Input
                  id="thumbnail"
                  type="text"
                  name="thumbnail"
                  placeholder="Paste image link"
                  value={blogForm.thumbnail}
                  onChange={handleBlogDataChange}
                  aria-label="Thumbnail URL"
                />
                <ThumbnailButton as="label" htmlFor="thumbnail-upload">
                  Upload
                  <input
                    id="thumbnail-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    style={{ display: "none" }}
                  />
                </ThumbnailButton>
              </AddThumbnailContainer>
              {thumbnail && <p tw="text-sm text-gray-600 mt-2">Selected file: {thumbnail.name}</p>}
            </div>
            <br />
            <div>
              <Label htmlFor="description">Short Description</Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Enter a brief description of the blog"
                value={blogForm.description}
                onChange={handleBlogDataChange}
                aria-label="Blog description"
              />
            </div>
            <br />
            <div>
              <Label htmlFor="category">Category</Label>
              <SelectInput
                id="category"
                type="text"
                name="category"
                placeholder="Type or select a category"
                list="categories"
                value={blogForm.category}
                onChange={handleBlogDataChange}
                aria-label="Blog category"
              />
              <datalist id="categories">
                {categories.map((cat, index) => (
                  <option key={index} value={cat} />
                ))}
              </datalist>
            </div>
            <br />
            <div>
              <Label htmlFor="hashtags">Hashtags</Label>
              <Input
                id="hashtags"
                type="text"
                name="hashtags"
                placeholder="Enter hashtags separated by commas"
                value={blogForm.hashtags}
                onChange={handleBlogDataChange}
                aria-label="Blog hashtags"
              />
            </div>
            <br />
            <SubmitButton type="submit" disabled={isSubmitting} onClick={handleBlogSubmit}>
              {isSubmitting ? <ProgressCircle /> : "Submit"}
            </SubmitButton>
          </FormSection>
          <ImageColumn>
            <Image imageSrc={BlogIllustrate} />
          </ImageColumn>
        </TwoColumn>

        {isModalOpen && (
          <Modal>
            <ModalContent>
              <CloseButton onClick={handleClose} />
              <h2>Enter Your Details</h2>
              <br />
              <form>
                <div>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    name="name"
                    value={userForm.name}
                    onChange={handleUserDataChange}
                    required
                    aria-label="User name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    name="email"
                    value={userForm.email}
                    onChange={handleUserDataChange}
                    required
                    aria-label="User email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    type="text"
                    name="mobile"
                    value={userForm.mobile}
                    onChange={handleUserDataChange}
                    required
                    aria-label="User phone number"
                  />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    type="text"
                    name="designation"
                    value={userForm.designation}
                    onChange={handleUserDataChange}
                    required
                    aria-label="User designation"
                  />
                </div>
                <br />
                <div>
                  <Label>City</Label>
                  <Input
                    type="text"
                    name="city"
                    value={userForm.city}
                    onChange={handleUserDataChange}
                    required
                    aria-label="User city"
                  />
                </div>
                <br />
                <div>
                  <Label htmlFor="profile">Profile Picture</Label>
                  <AddThumbnailContainer>
                    <Input
                      id="profile"
                      type="text"
                      name="profile_pic"
                      placeholder="Paste profile link"
                      value={userForm.profile_pic}
                      onChange={handleUserDataChange}
                      aria-label="Profile picture URL"
                    />
                    <ThumbnailButton as="label" htmlFor="profile-upload">
                      Upload
                      <input
                        id="profile-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleProfileUpload}
                        style={{ display: "none" }}
                      />
                    </ThumbnailButton>
                  </AddThumbnailContainer>
                  {profile && <p tw="text-sm text-gray-600 mt-2">Selected file: {profile.name}</p>}
                </div>
                <br />
                <SubmitButton type="submit" disabled={isSubmitting} onClick={handleUserSubmit}>
                  {isSubmitting ? <ProgressCircle /> : "Submit Blog"}
                </SubmitButton>
              </form>
            </ModalContent>
          </Modal>
        )}

        <DialogBox show={showDialog}>
          <DialogContent>
            <DialogHeading className="dialog-heading">
              {isSuccess ? (
                <FaCheckCircle className="success-icon" />
              ) : fieldError ? (
                <FaInfoCircle className="info-icon" />
              ) : (
                <FaTimesCircle className="error-icon" />
              )}
            </DialogHeading>
            <DialogMessage>
              {isSuccess ? "Thank you for your submission." : fieldError ? message : "We're facing issue submitting your blog. Please try again."}
            </DialogMessage>
            <OkButton onClick={handleCloseDialog}>OK</OkButton>
          </DialogContent>
        </DialogBox>
      </Container>
      <FooterCard />
    </AnimationRevealPage>
  );
};